/**
 * Production-safe logger utility.
 *
 * In development: all logs are visible
 * In production: only warn/error are visible (no debug/info noise)
 *
 * Features:
 * - Namespaced loggers for different modules
 * - Automatic environment detection
 * - Consistent prefix formatting
 * - Type-safe API
 *
 * @example
 * ```ts
 * // Create a namespaced logger
 * const log = createLogger("Storage");
 *
 * log.debug("Token found", { expiresAt });  // dev only
 * log.info("User authenticated");           // dev only
 * log.warn("Token expires soon");           // dev + prod
 * log.error("Failed to save", error);       // dev + prod
 * ```
 */

import { ENV_CONFIG } from "../config";

// =============================================================================
// Types
// =============================================================================

type LogLevel = "debug" | "info" | "warn" | "error";

interface Logger {
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

// =============================================================================
// Configuration
// =============================================================================

/**
 * Log levels that are enabled in production.
 * Debug and info are disabled to reduce console noise.
 */
const PROD_ENABLED_LEVELS: Set<LogLevel> = new Set(["warn", "error"]);

/**
 * Check if logging is enabled for a given level.
 */
function isEnabled(level: LogLevel): boolean {
  // Development: all levels enabled
  if (ENV_CONFIG.isDev) return true;

  // Production: only warn and error
  return PROD_ENABLED_LEVELS.has(level);
}

// =============================================================================
// Logger Factory
// =============================================================================

/**
 * Create a namespaced logger.
 *
 * @param namespace - Module name to prefix logs with (e.g., "Storage", "Auth", "PWA")
 * @returns Logger instance with debug/info/warn/error methods
 *
 * @example
 * ```ts
 * const log = createLogger("Auth");
 * log.info("User logged in", { userId: "123" });
 * // Output: [Auth] User logged in { userId: "123" }
 * ```
 */
export function createLogger(namespace: string): Logger {
  const prefix = `[${namespace}]`;

  return {
    debug: (...args: unknown[]) => {
      if (isEnabled("debug")) {
        console.debug(prefix, ...args);
      }
    },
    info: (...args: unknown[]) => {
      if (isEnabled("info")) {
        console.info(prefix, ...args);
      }
    },
    warn: (...args: unknown[]) => {
      if (isEnabled("warn")) {
        console.warn(prefix, ...args);
      }
    },
    error: (...args: unknown[]) => {
      if (isEnabled("error")) {
        console.error(prefix, ...args);
      }
    },
  };
}

// =============================================================================
// Pre-configured Loggers
// =============================================================================

/** Logger for storage operations (tokens, localStorage, etc.) */
export const storageLog = createLogger("Storage");

/** Logger for authentication flow */
export const authLog = createLogger("Auth");

/** Logger for PWA installation and service worker */
export const pwaLog = createLogger("PWA");

/** Logger for GraphQL/API operations */
export const apiLog = createLogger("API");

/** Logger for circuit breaker state changes */
export const circuitLog = createLogger("CircuitBreaker");

/** Logger for Telegram Mini App integration */
export const telegramLog = createLogger("Telegram");

/** Logger for admin panel operations */
export const adminLog = createLogger("Admin");

// =============================================================================
// Default Export
// =============================================================================

/**
 * Default logger (no namespace).
 * Use createLogger() for namespaced logging.
 */
export const logger = createLogger("App");
